import { useState } from 'react';

import { Grid, InputAdornment, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { SearchRounded as SearchIcon } from '@material-ui/icons';

import { useFindTags } from '../../hooks/admin-find-tags.hook';
import { useCreateTag } from '../../hooks/create-tag.hook';
import { useUpdateTag } from '../../hooks/update-tag.hook';
import { useDeleteTag } from '../../hooks/delete-tag.hook';
import { Tag } from '../../types/tag.type';
import TagsTable from '../../components/Tables/TagsTable.component';
import { CreateTagCard } from '../../components/Forms/CreateTagCard.component';
import { useDebounce } from '../../../shared/hooks/use-debounce.hook';
import { DEFAULT_ROWS_PER_PAGE } from '../../../shared/constants/table.constants';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },

    '& h4': {
      margin: '0 0 0.5rem 0',
      opacity: 0.7,
    },

    '& .MuiButton-root': {
      textTransform: 'none',
    },

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  listColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',
  },
  searchInput: {
    maxWidth: 320,
  },
}));

export default function AdminTagsView() {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [nameSearch, setNameSearch] = useState('');
  const debouncedNameSearch = useDebounce(nameSearch, 400);

  const { isLoading, isRefetching, tags, totalCount, refetch } = useFindTags({
    page: page + 1,
    limit: rowsPerPage,
    name: debouncedNameSearch || undefined,
  });

  const { isSubmitting: isCreating, createTag } = useCreateTag();
  const { isSubmitting: isUpdating, updateTag } = useUpdateTag();
  const { isSubmitting: isDeleting, deleteTag } = useDeleteTag();

  const handleSearchChange = (value: string) => {
    setNameSearch(value);
    setPage(0);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleCreate = async (name: string): Promise<boolean> => {
    const createdTag = await createTag(name);
    if (!createdTag) return false;

    await refetch();
    return true;
  };

  const handleRename = async (tag: Tag, name: string): Promise<boolean> => {
    const renamedTag = await updateTag(tag.id, { name });
    if (!renamedTag) return false;

    await refetch();
    return true;
  };

  const handleDelete = async (tag: Tag): Promise<boolean> => {
    const isDeleted = await deleteTag(tag.id);
    if (!isDeleted) return false;

    if (page > 0 && tags.length === 1) {
      setPage(page - 1);
    } else {
      await refetch();
    }
    return true;
  };

  return (
    <div className={classes.root}>
      <h2>Tags</h2>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8} className={classes.listColumn}>
          <TextField
            className={classes.searchInput}
            size="small"
            variant="outlined"
            label="Rechercher un tag"
            value={nameSearch}
            onChange={(event) => handleSearchChange(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TagsTable
            tags={tags}
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            isLoading={isLoading || isRefetching}
            isSubmitting={isUpdating || isDeleting}
            onPageChange={setPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            onRename={handleRename}
            onDelete={handleDelete}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <h4>Créer un tag</h4>
          <CreateTagCard isSubmitting={isCreating} onCreate={handleCreate} />
        </Grid>
      </Grid>
    </div>
  );
}
